// Column definitions and row cells for the Materials overview table.
//
// WHY THIS IS ITS OWN MODULE. `Table` renders `columns` for the header and a
// bare fragment of `TableCell`s for the body; nothing connects the two but
// their order. Add a column without a matching cell and every value below it
// silently shifts one column to the left — the build passes, the types pass,
// and the page looks plausible. Keeping both halves here lets a test assert
// they stay the same length, which is the only real guard against that.
//
// It also keeps the pair importable without firebase/config, so the test does
// not have to boot the SDK.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'

import TableCell from '../../components/common/table/TableCell'
import TableTitleCell from '../../components/common/table/TableTitleCell'
import Tooltip from '../../components/common/Tooltip'
import stockColor from '../../helpers/materialsHelpers/stockColor'
import materialDisplayName, { materialArticleNumber } from '../../helpers/materialsHelpers/materialDisplayName'
import { isAdvice } from '../../helpers/materialsHelpers/materialCatalog'

/**
 * @typedef {object} MaterialsColumn
 * @property {string} key
 * @property {string} label
 * @property {boolean} [searchable]
 */

/** @type {Array<MaterialsColumn>} */
const MATERIALS_COLUMNS = [
    { key: 'title', label: 'MATERIAAL OF ADVIES' },
    { key: 'category', label: 'CATEGORIE', searchable: false },
    { key: 'stock', label: 'VOORRAAD', searchable: false },
    { key: 'unit', label: 'EENHEID', searchable: false },
    { key: 'threshold', label: 'LAAG VANAF', searchable: false },
    { key: 'delete', label: 'VERWIJDER', searchable: false },
]

/**
 * The body cells for one material, in the same order as MATERIALS_COLUMNS.
 *
 * Returns an array rather than a fragment so a test can count it.
 *
 * @param {object} m - A Materials doc
 * @param {object} handlers
 * @param {boolean} handlers.canEdit
 * @param {(m: object) => void} handlers.openEdit
 * @param {(e: object) => void} handlers.deleteModalHandler
 * @returns {Array<JSX.Element>}
 */
const materialRowCells = (m, { canEdit, openEdit, deleteModalHandler }) => {
    const color = stockColor(m.QuantityInStock ?? 0, m.LowStockThreshold ?? 0)
    // De eigen naam van de organisatie wint van de standaardnaam; zonder
    // eigen naam is dit gewoon de standaardnaam.
    const naam = materialDisplayName(m)
    const artikelnummer = materialArticleNumber(m)
    // Een coachadvies wordt niet ingekocht: het heeft geen voorraad en geen
    // drempel. Een stip op 0 zou hier lezen als 'op', en dat is iets anders dan
    // 'niet van toepassing'. De eenheid blijft wel staan — dat is de
    // registratie-eenheid van de meetstandaard ('per huishouden').
    const advice = isAdvice(m)

    return [
        // Aanpassen gaat via de titel, zoals overal in de app. Het potloodje van
        // TableTitleCell staat uit (`readOnly`): dat hernoemt ter plekke, en de
        // naam komt hier juist uit de vaste keuzelijst van de meetstandaard.
        // Vrij overtypen zou precies de losse namen terugbrengen die die lijst
        // moest wegnemen.
        //
        // De 'Coachadvies'-tag en het productnummer hangen onder de titel en niet
        // erin: een knop plat ARIA zijn kinderen tot een naam, dus binnenin zou
        // de knop 'Tochtband 9mm zwart GS359219' gaan heten.
        // AI-CONTEXT: docs/pitfalls.md#ui-heading-inside-button-stops-being-a-heading
        <TableTitleCell
            key='title'
            value={naam}
            readOnly
            onTitleClick={canEdit ? () => openEdit(m) : undefined}
            footer={advice
                ? <span className='materials-kind-tag'>Coachadvies</span>
                : (artikelnummer
                    ? <span className='materials-article-number'>{artikelnummer}</span>
                    : null)}
        />,
        <TableCell key='category'>
            <p>{m.Category || ''}</p>
        </TableCell>,
        <TableCell key='stock'>
            {advice ? <p>—</p> : (
                <div className='materials-stock-cell'>
                    <span className={`materials-stock-dot materials-stock-dot-${color}`} />
                    <span>{m.QuantityInStock ?? 0}</span>
                </div>
            )}
        </TableCell>,
        <TableCell key='unit'>
            <p>{m.Unit || ''}</p>
        </TableCell>,
        <TableCell key='threshold'>
            <p>{advice ? '—' : (m.LowStockThreshold ?? 0)}</p>
        </TableCell>,
        <TableCell key='delete'>
            {canEdit && (
                <Tooltip content={advice ? 'Coachadvies verwijderen' : 'Materiaal verwijderen'} top='-60px' width={'30px'}>
                    <DeleteOutlinedIcon
                        className='table-delete-icon'
                        data-docid={m.docid}
                        data-deletename={naam}
                        onClick={deleteModalHandler}
                    />
                </Tooltip>
            )}
        </TableCell>,
    ]
}

export { MATERIALS_COLUMNS, materialRowCells }
